<script setup lang="ts">
/**
 * Ligne de liste : visuel à gauche, titre et sous-titre, valeur à droite. Touchable si elle
 * mène quelque part (`to`) ou déclenche une action (`interactive`). Les filets sont posés par
 * le conteneur (`divide-y divide-line`), jamais par des cartes.
 */
import { computed } from 'vue'
import { RouterLink, type RouteLocationRaw } from 'vue-router'

import type { IconName } from '../icons/registry'
import Icon from './Icon.vue'

const props = withDefaults(
  defineProps<{
    title: string
    subtitle?: string
    icon?: IconName
    to?: RouteLocationRaw
    interactive?: boolean
    chevron?: boolean
  }>(),
  { subtitle: undefined, icon: undefined, to: undefined, interactive: false, chevron: false },
)

defineEmits<{ click: [event: MouseEvent] }>()

const tag = computed(() => (props.to ? RouterLink : props.interactive ? 'button' : 'div'))
const touchable = computed(() => props.to !== undefined || props.interactive)
</script>

<template>
  <component
    :is="tag"
    :to="to"
    :type="tag === 'button' ? 'button' : undefined"
    :class="[
      'flex min-h-control w-full items-center gap-3 py-3 text-left',
      touchable ? 'transition-colors duration-80 active:bg-surface' : '',
    ]"
    @click="(event: MouseEvent) => $emit('click', event)"
  >
    <slot name="leading">
      <span
        v-if="icon"
        class="flex size-11 shrink-0 items-center justify-center rounded-full bg-surface text-ink"
        aria-hidden="true"
      >
        <Icon :name="icon" :size="20" />
      </span>
    </slot>
    <span class="flex min-w-0 flex-1 flex-col gap-0.5">
      <span class="truncate text-body text-ink">{{ title }}</span>
      <span v-if="subtitle || $slots.subtitle" class="truncate text-caption text-ink-muted">
        <slot name="subtitle">{{ subtitle }}</slot>
      </span>
    </span>
    <slot name="trailing" />
    <Icon v-if="chevron" name="forward" class="text-ink-muted" />
  </component>
</template>
